import {
  parseAssessedNumber,
  sanitizeAssessmentParcelForFirestore,
} from "./calgaryAssessmentOpenData";
import { formatMyPropertyAddressLine } from "./nominatimGeocode";

function safeStr(value) {
  return String(value ?? "").trim();
}

function titleCaseValue(value) {
  return safeStr(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function cleanDate(value) {
  const s = safeStr(value);
  if (!s) return null;
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function pickDisplay(clientValue, crmValue, fallback = null) {
  const c = safeStr(clientValue);
  if (c) return { value: c, source: "client" };
  const r = safeStr(crmValue);
  if (r) return { value: r, source: "crm" };
  const f = safeStr(fallback);
  if (f) return { value: f, source: "fallback" };
  return { value: null, source: null };
}

function normalizeGeocode(raw) {
  if (!raw || typeof raw !== "object") return null;
  const lat = Number(raw.lat);
  const lng = Number(raw.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return {
    lat,
    lng,
    displayName: safeStr(raw.displayName) || null,
  };
}

function normalizeParcelSnapshot(raw) {
  if (!raw || typeof raw !== "object") return null;
  return sanitizeAssessmentParcelForFirestore(raw);
}

const cadFmt = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
  maximumFractionDigits: 0,
});

export const SALE_READINESS_CHECKLIST = [
  "Confirm title / legal name",
  "Gather mortgage details",
  "Gather insurance details",
  "Gather renovation / permit history",
  "Identify lawyer",
  "Review market timing with Mark",
];

/** Keys clients may write on users/{uid} for the RE property file (not CRM fields). */
export const CLIENT_MAINTAINED_PROPERTY_FIELD_KEYS = [
  "myPropertyAddressLine",
  "myPropertyFullAddress",
  "myPropertyUnit",
  "myPropertyStreetLine",
  "myPropertyStreetAddress",
  "myPropertyCity",
  "myPropertyProvince",
  "myPropertyPostal",
  "myPropertyPostalCode",
  "myPropertyLat",
  "myPropertyLng",
  "myPropertyGeocode",
  "myPropertyParcelSnapshot",
  "myPropertyParcelRoll",
  "myPropertyParcelYear",
  "myPropertyParcelCommunity",
  "myPropertyParcelAssessedValue",
  "myPropertyType",
  "myPropertyNeighbourhood",
  "myPropertyHomeownerStatus",
  "myPropertyMortgageRenewalDate",
  "myPropertyInsuranceRenewalDate",
  "myPropertyLawyerName",
  "myPropertyMortgageBrokerName",
  "myPropertyInsuranceContactName",
  "myPropertyNotes",
];

export function getDefaultClientPropertyForm() {
  return {
    myPropertyAddressLine: "",
    myPropertyFullAddress: "",
    myPropertyUnit: "",
    myPropertyStreetLine: "",
    myPropertyStreetAddress: "",
    myPropertyCity: "",
    myPropertyProvince: "",
    myPropertyPostalCode: "",
    myPropertyLat: "",
    myPropertyLng: "",
    myPropertyGeocode: null,
    myPropertyParcelSnapshot: null,
    myPropertyParcelRoll: "",
    myPropertyParcelYear: "",
    myPropertyParcelCommunity: "",
    myPropertyParcelAssessedValue: "",
    myPropertyType: "",
    myPropertyNeighbourhood: "",
    myPropertyHomeownerStatus: "",
    myPropertyMortgageRenewalDate: "",
    myPropertyInsuranceRenewalDate: "",
    myPropertyLawyerName: "",
    myPropertyMortgageBrokerName: "",
    myPropertyInsuranceContactName: "",
    myPropertyNotes: "",
  };
}

/** Lookup form fields for Calgary parcel match UI. */
export function clientPropertyAddressLookupFromProfile(profile) {
  const p = profile || {};
  return {
    unit: safeStr(p.myPropertyUnit),
    streetLine: safeStr(p.myPropertyStreetLine) || safeStr(p.myPropertyStreetAddress),
    city: safeStr(p.myPropertyCity) || "Calgary",
    province: safeStr(p.myPropertyProvince) || "AB",
    postalCode: safeStr(p.myPropertyPostalCode) || safeStr(p.myPropertyPostal),
  };
}

export function clientPropertyFormFromProfile(profile) {
  const p = profile || {};
  const base = getDefaultClientPropertyForm();
  const assessed = p.myPropertyParcelAssessedValue;
  return {
    ...base,
    myPropertyAddressLine: safeStr(p.myPropertyAddressLine),
    myPropertyFullAddress: safeStr(p.myPropertyFullAddress) || safeStr(p.myPropertyAddressLine),
    myPropertyUnit: safeStr(p.myPropertyUnit),
    myPropertyStreetLine: safeStr(p.myPropertyStreetLine) || safeStr(p.myPropertyStreetAddress),
    myPropertyStreetAddress: safeStr(p.myPropertyStreetAddress) || safeStr(p.myPropertyStreetLine),
    myPropertyCity: safeStr(p.myPropertyCity),
    myPropertyProvince: safeStr(p.myPropertyProvince),
    myPropertyPostalCode: safeStr(p.myPropertyPostalCode) || safeStr(p.myPropertyPostal),
    myPropertyLat: p.myPropertyLat != null ? String(p.myPropertyLat) : "",
    myPropertyLng: p.myPropertyLng != null ? String(p.myPropertyLng) : "",
    myPropertyGeocode: p.myPropertyGeocode || null,
    myPropertyParcelSnapshot: p.myPropertyParcelSnapshot || null,
    myPropertyParcelRoll: safeStr(p.myPropertyParcelRoll),
    myPropertyParcelYear: safeStr(p.myPropertyParcelYear),
    myPropertyParcelCommunity: safeStr(p.myPropertyParcelCommunity),
    myPropertyParcelAssessedValue:
      assessed != null && assessed !== "" ? String(assessed) : "",
    myPropertyType: safeStr(p.myPropertyType),
    myPropertyNeighbourhood: safeStr(p.myPropertyNeighbourhood),
    myPropertyHomeownerStatus: safeStr(p.myPropertyHomeownerStatus),
    myPropertyMortgageRenewalDate: cleanDate(p.myPropertyMortgageRenewalDate) || "",
    myPropertyInsuranceRenewalDate: cleanDate(p.myPropertyInsuranceRenewalDate) || "",
    myPropertyLawyerName: safeStr(p.myPropertyLawyerName),
    myPropertyMortgageBrokerName: safeStr(p.myPropertyMortgageBrokerName),
    myPropertyInsuranceContactName: safeStr(p.myPropertyInsuranceContactName),
    myPropertyNotes: safeStr(p.myPropertyNotes),
  };
}

/**
 * Firestore address + parcel fields from a successful Calgary parcel match.
 */
export function buildClientPropertyAddressFromMatch({
  unit,
  streetLine,
  city,
  postalCode,
  province,
  geocode,
  parcel,
}) {
  const postal = safeStr(postalCode);
  const cityNorm = safeStr(city) || "Calgary";
  const prov = safeStr(province) || "AB";
  const street = safeStr(streetLine);
  const formatted = formatMyPropertyAddressLine({
    unit,
    street,
    city: cityNorm,
    postal,
  });
  const parcelSafe = parcel ? sanitizeAssessmentParcelForFirestore(parcel) : null;
  const assessed = parcel ? parseAssessedNumber(parcel.assessed_value ?? parcel.ASSESSMENT) : null;

  return {
    myPropertyAddressLine: formatted || null,
    myPropertyFullAddress: formatted || null,
    myPropertyUnit: safeStr(unit) || null,
    myPropertyStreetLine: street || null,
    myPropertyStreetAddress: street || null,
    myPropertyCity: cityNorm,
    myPropertyProvince: prov,
    myPropertyPostalCode: postal || null,
    myPropertyPostal: postal || null,
    myPropertyLat: geocode?.ok ? geocode.lat : null,
    myPropertyLng: geocode?.ok ? geocode.lng : null,
    myPropertyGeocode: geocode?.ok
      ? {
          lat: geocode.lat,
          lng: geocode.lng,
          displayName: geocode.displayName || null,
        }
      : null,
    myPropertyParcelSnapshot: parcelSafe,
    myPropertyParcelRoll: safeStr(parcel?.roll_number) || safeStr(parcel?.roll) || null,
    myPropertyParcelYear: parcel?.roll_year != null ? String(parcel.roll_year) : null,
    myPropertyParcelCommunity: safeStr(parcel?.comm_name) || null,
    myPropertyParcelAssessedValue: assessed,
  };
}

/** One-line parcel match confirmation for UI. */
export function formatParcelMatchSummaryLine(data) {
  const parts = [];
  const roll = safeStr(data?.myPropertyParcelRoll);
  if (roll) parts.push(`Roll ${roll}`);
  const comm = safeStr(data?.myPropertyParcelCommunity);
  if (comm) parts.push(comm);
  const val = data?.myPropertyParcelAssessedValue;
  const n = val != null && val !== "" ? Number(val) : null;
  if (n != null && Number.isFinite(n)) {
    parts.push(`Assessed ${cadFmt.format(n)}`);
  }
  return parts.length ? parts.join(" · ") : null;
}

/**
 * @param {object|null|undefined} raw — edit form state
 */
export function normalizeClientPropertyInput(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const postal = safeStr(r.myPropertyPostalCode) || safeStr(r.myPropertyPostal);
  const street = safeStr(r.myPropertyStreetLine) || safeStr(r.myPropertyStreetAddress);
  const latRaw = r.myPropertyLat;
  const lngRaw = r.myPropertyLng;
  const lat =
    latRaw === "" || latRaw == null
      ? null
      : Number.isFinite(Number(latRaw))
        ? Number(latRaw)
        : null;
  const lng =
    lngRaw === "" || lngRaw == null
      ? null
      : Number.isFinite(Number(lngRaw))
        ? Number(lngRaw)
        : null;
  const assessedRaw = r.myPropertyParcelAssessedValue;
  const assessed =
    assessedRaw === "" || assessedRaw == null
      ? null
      : Number.isFinite(Number(assessedRaw))
        ? Number(assessedRaw)
        : null;

  return {
    myPropertyAddressLine: safeStr(r.myPropertyAddressLine) || null,
    myPropertyFullAddress: safeStr(r.myPropertyFullAddress) || safeStr(r.myPropertyAddressLine) || null,
    myPropertyUnit: safeStr(r.myPropertyUnit) || null,
    myPropertyStreetLine: street || null,
    myPropertyStreetAddress: street || null,
    myPropertyCity: safeStr(r.myPropertyCity) || null,
    myPropertyProvince: safeStr(r.myPropertyProvince) || null,
    myPropertyPostalCode: postal || null,
    myPropertyPostal: postal || null,
    myPropertyLat: lat,
    myPropertyLng: lng,
    myPropertyGeocode: normalizeGeocode(r.myPropertyGeocode),
    myPropertyParcelSnapshot: normalizeParcelSnapshot(r.myPropertyParcelSnapshot),
    myPropertyParcelRoll: safeStr(r.myPropertyParcelRoll) || null,
    myPropertyParcelYear: safeStr(r.myPropertyParcelYear) || null,
    myPropertyParcelCommunity: safeStr(r.myPropertyParcelCommunity) || null,
    myPropertyParcelAssessedValue: assessed,
    myPropertyType: safeStr(r.myPropertyType) || null,
    myPropertyNeighbourhood: safeStr(r.myPropertyNeighbourhood) || null,
    myPropertyHomeownerStatus: safeStr(r.myPropertyHomeownerStatus) || null,
    myPropertyMortgageRenewalDate: cleanDate(r.myPropertyMortgageRenewalDate),
    myPropertyInsuranceRenewalDate: cleanDate(r.myPropertyInsuranceRenewalDate),
    myPropertyLawyerName: safeStr(r.myPropertyLawyerName) || null,
    myPropertyMortgageBrokerName: safeStr(r.myPropertyMortgageBrokerName) || null,
    myPropertyInsuranceContactName: safeStr(r.myPropertyInsuranceContactName) || null,
    myPropertyNotes: safeStr(r.myPropertyNotes) || null,
  };
}

/** Firestore patch for client property file only (timestamps added by caller). */
export function buildClientPropertyUpdatePayload(input) {
  return normalizeClientPropertyInput(input);
}

function formatMailingAddress(profile) {
  const line1 = safeStr(profile?.mailingAddressLine1);
  const line2 = safeStr(profile?.mailingAddressLine2);
  const cityLine = [safeStr(profile?.mailingCity), safeStr(profile?.mailingProvince), safeStr(profile?.mailingPostal)]
    .filter(Boolean)
    .join(", ");
  const parts = [line1, line2, cityLine].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

/**
 * Best available property address for display (client-maintained first).
 */
export function resolveClientPropertyAddress(profile) {
  const p = profile || {};
  const full = safeStr(p.myPropertyFullAddress);
  if (full) return full;

  const built = formatMyPropertyAddressLine({
    unit: p.myPropertyUnit,
    street: p.myPropertyStreetLine || p.myPropertyStreetAddress,
    city: p.myPropertyCity,
    postal: p.myPropertyPostalCode || p.myPropertyPostal,
  });
  if (safeStr(p.myPropertyStreetLine || p.myPropertyStreetAddress) && built) {
    return built;
  }

  const line = safeStr(p.myPropertyAddressLine);
  if (line && /calgary/i.test(line)) return line;

  const city = safeStr(p.myPropertyCity);
  const prov = safeStr(p.myPropertyProvince);
  const postal = safeStr(p.myPropertyPostalCode) || safeStr(p.myPropertyPostal);
  const cityLine = [city, prov, postal].filter(Boolean).join(", ");
  if (line && cityLine) return `${line}, ${cityLine}`;
  if (line) return line;
  if (cityLine) return cityLine;

  const crm = safeStr(p.propertyAddress);
  if (crm) return crm;

  return formatMailingAddress(p);
}

/**
 * Merged display snapshot: client myProperty* → CRM → mailing fallback.
 */
export function getClientPropertySnapshot(profile) {
  const p = profile || {};
  const typePick = pickDisplay(p.myPropertyType, p.propertyType);
  const neighbourhoodPick = pickDisplay(p.myPropertyNeighbourhood, p.neighbourhood);
  const homeownerPick = pickDisplay(p.myPropertyHomeownerStatus, p.currentHomeownerStatus);
  const mortgagePick = pickDisplay(p.myPropertyMortgageRenewalDate, p.mortgageRenewalDate);
  const insurancePick = pickDisplay(p.myPropertyInsuranceRenewalDate, p.insuranceRenewalDate);
  const lawyerPick = pickDisplay(p.myPropertyLawyerName, p.lawyerName);
  const brokerPick = pickDisplay(p.myPropertyMortgageBrokerName, p.mortgageBrokerName);
  const insuranceContactPick = pickDisplay(p.myPropertyInsuranceContactName, null);

  const addressLinePick = pickDisplay(
    p.myPropertyFullAddress || p.myPropertyAddressLine,
    p.propertyAddress
  );
  const address = resolveClientPropertyAddress(p);
  const parcelMatchLine = formatParcelMatchSummaryLine(p);

  return {
    address,
    addressLine: addressLinePick.value,
    city: safeStr(p.myPropertyCity) || safeStr(p.mailingCity) || null,
    province: safeStr(p.myPropertyProvince) || safeStr(p.mailingProvince) || null,
    postalCode: safeStr(p.myPropertyPostalCode) || safeStr(p.myPropertyPostal) || safeStr(p.mailingPostal) || null,
    parcelMatchLine,
    propertyType: typePick.value ? titleCaseValue(typePick.value) : null,
    neighbourhood: neighbourhoodPick.value || null,
    homeownerStatus: homeownerPick.value ? titleCaseValue(homeownerPick.value) : null,
    mortgageRenewalDate: mortgagePick.value || null,
    insuranceRenewalDate: insurancePick.value || null,
    targetMoveDate: safeStr(p.targetMoveDate) || null,
    lawyerName: lawyerPick.value || null,
    mortgageBrokerName: brokerPick.value || null,
    insuranceContactName: insuranceContactPick.value || null,
    clientNotes: safeStr(p.myPropertyNotes) || null,
    sources: {
      address: addressLinePick.source || (address ? "fallback" : null),
      propertyType: typePick.source,
      neighbourhood: neighbourhoodPick.source,
      homeownerStatus: homeownerPick.source,
      mortgageRenewalDate: mortgagePick.source,
      insuranceRenewalDate: insurancePick.source,
      lawyerName: lawyerPick.source,
      mortgageBrokerName: brokerPick.source,
      insuranceContactName: insuranceContactPick.source,
    },
  };
}

/** @deprecated Use getClientPropertySnapshot */
export function buildClientPropertySnapshot(profile) {
  return getClientPropertySnapshot(profile);
}

export function hasClientPropertySnapshot(snapshot) {
  if (!snapshot) return false;
  return Boolean(
    snapshot.address ||
      snapshot.propertyType ||
      snapshot.neighbourhood ||
      snapshot.homeownerStatus ||
      snapshot.mortgageRenewalDate ||
      snapshot.insuranceRenewalDate ||
      snapshot.targetMoveDate ||
      snapshot.lawyerName ||
      snapshot.mortgageBrokerName ||
      snapshot.insuranceContactName ||
      snapshot.clientNotes
  );
}

/** True when the client has saved at least one myProperty* field. */
export function hasClientMaintainedProperty(profile) {
  const form = clientPropertyFormFromProfile(profile);
  return CLIENT_MAINTAINED_PROPERTY_FIELD_KEYS.some((key) => {
    const v = form[key];
    if (v == null) return false;
    if (typeof v === "object") return Boolean(v);
    return safeStr(v);
  });
}

export function formatClientPropertyField(value, { emptyLabel = "Not added yet" } = {}) {
  const s = safeStr(value);
  return s || emptyLabel;
}

export function formatClientPropertyLabel(value) {
  const s = safeStr(value);
  return s ? titleCaseValue(s) : null;
}

export function sourceHint(source) {
  if (source === "crm") return "Shown from your file on record until you add your own.";
  if (source === "fallback") return "Shown from mailing address until you add property details.";
  return null;
}

export function isClientPropertyFileComplete(profile) {
  return hasClientMaintainedProperty(profile);
}
